import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";

import { useIsMobile } from "@/hooks/use-mobile";
import { isKeyboardOpen } from "@/hooks/useVisualViewport";

/**
 * Mobile behaviour shared by every create/edit sheet:
 *
 * - Back (button, edge swipe, Android back) closes the form instead of
 *   leaving the page: opening pushes a same-URL history entry, popping it
 *   closes the topmost open form only. Closing any other way (x, Escape,
 *   save) consumes that entry again so history stays balanced.
 * - `data-mobile-form-open` on <body> while a form is open, so the floating
 *   chrome (top row, tab bar, +) can hide itself from CSS.
 * - `--mobile-form-vvh` / `--mobile-form-vvtop` on <html> follow the visual
 *   viewport, so the sheet shrinks above the iOS keyboard and its header and
 *   Save button stay on screen; `data-mobile-keyboard` marks the keyboard.
 *
 * Routed forms (the URL already has its own history entry) pass
 * `history: false`.
 */

type Entry = { id: string; close: () => void };

const stack: Entry[] = [];
// Pops we triggered ourselves to drop a consumed entry: not a user "back".
let selfPops = 0;
let listening = false;

const onPopState = () => {
  if (selfPops > 0) {
    selfPops -= 1;
    return;
  }
  stack[stack.length - 1]?.close();
};

const ensureListener = () => {
  if (listening) return;
  window.addEventListener("popstate", onPopState);
  listening = true;
};

const syncViewport = () => {
  const root = document.documentElement;
  const viewport = window.visualViewport;
  if (!viewport) return;
  root.style.setProperty("--mobile-form-vvh", `${viewport.height}px`);
  root.style.setProperty("--mobile-form-vvtop", `${viewport.offsetTop}px`);
  if (isKeyboardOpen()) {
    root.dataset.mobileKeyboard = "";
  } else {
    delete root.dataset.mobileKeyboard;
  }
};

const clearViewport = () => {
  const root = document.documentElement;
  root.style.removeProperty("--mobile-form-vvh");
  root.style.removeProperty("--mobile-form-vvtop");
  delete root.dataset.mobileKeyboard;
};

let nextId = 0;

export const useMobileFormGuard = (
  open: boolean,
  onClose: () => void,
  { history = true }: { history?: boolean } = {},
) => {
  const isMobile = useIsMobile();
  const active = open && isMobile;
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Survives StrictMode's unmount/remount so the entry is pushed only once.
  const entryRef = useRef<{ id: string; release?: number } | null>(null);

  useEffect(() => {
    if (!active) return;
    ensureListener();

    let entry = entryRef.current;
    if (entry?.release != null) {
      window.clearTimeout(entry.release);
      entry.release = undefined;
    } else {
      entry = { id: `mobile-form-${++nextId}` };
      entryRef.current = entry;
      if (history) {
        window.history.pushState(
          { ...window.history.state, mobileForm: entry.id },
          "",
        );
      }
    }
    const current = entry;

    const stackEntry: Entry = {
      id: current.id,
      close: () => {
        // The entry is already gone: don't consume another one on cleanup.
        entryRef.current = null;
        onCloseRef.current();
      },
    };
    stack.push(stackEntry);
    document.body.dataset.mobileFormOpen = "";

    const viewport = window.visualViewport;
    syncViewport();
    viewport?.addEventListener("resize", syncViewport);
    viewport?.addEventListener("scroll", syncViewport);

    return () => {
      const index = stack.indexOf(stackEntry);
      if (index !== -1) stack.splice(index, 1);
      viewport?.removeEventListener("resize", syncViewport);
      viewport?.removeEventListener("scroll", syncViewport);
      if (stack.length === 0) {
        delete document.body.dataset.mobileFormOpen;
        clearViewport();
      }

      if (entryRef.current !== current) return;
      // Deferred so a StrictMode remount can reclaim the entry.
      current.release = window.setTimeout(() => {
        if (entryRef.current === current) entryRef.current = null;
        if (history && window.history.state?.mobileForm === current.id) {
          selfPops += 1;
          window.history.back();
        }
      }, 0);
    };
  }, [active, history]);
};

/**
 * Close handler for a form rendered by its own route (e.g. /contacts/create):
 * go back when the app has history, otherwise fall back to `to` so a deep
 * link never strands the user.
 */
export const useCloseRoutedForm = (to: string) => {
  const navigate = useNavigate();
  const location = useLocation();
  return () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(to, { replace: true });
    }
  };
};
